import produce, { setAutoFreeze } from 'immer';

import { ISeatsResponse } from '../../types/types';
import { FloorplanData, Seat } from '../components/FloorPlan';

// Interface for seat with section information
interface SeatWithSection {
    row: number;
    number: number;
    section_id?: string;
    section_name?: string;
}

const findSeat = (data: FloorplanData, rowNumber: number, seatNumber: number, sectionId?: string): Seat | null => {
    for (const grid of data.grids) {
        if (sectionId && grid.sectionId !== sectionId) {
            continue;
        }

        for (const row of grid.rows) {
            if (row.number !== rowNumber) {
                continue;
            }

            for (const seat of row.seats) {
                if (seat?.number === seatNumber) {
                    return seat;
                }
            }
        }
    }

    return null;
};

export const applyStateToFloorplan = (
    floorplan: FloorplanData,
    seats: ISeatsResponse,
): FloorplanData => {
    setAutoFreeze(false);

    return produce(floorplan, (draft) => {
        // Use immer to create a copy instead of modifying the base floorplan in state

        // Then apply current states
        for (const seat of seats.booked) {
            const floorplanSeat = findSeat(
                draft,
                seat.row,
                seat.number,
                (seat as SeatWithSection).section_id
            );

            if (floorplanSeat) {
                floorplanSeat.isBooked = true;
            } else {
                console.warn('Seat not found:', seat.row, seat.number, (seat as SeatWithSection).section_id);
            }
        }

        // Apply sold state (ticket office)
        if (seats.sold) {
            for (const seat of seats.sold) {
                const floorplanSeat = findSeat(
                    draft,
                    seat.row,
                    seat.number,
                    (seat as SeatWithSection).section_id
                );

                if (floorplanSeat) {
                    floorplanSeat.isSold = true;
                } else {
                    console.warn('Sold seat not found:', seat.row, seat.number, (seat as SeatWithSection).section_id);
                }
            }
        }

        // Apply reserved state (ticket office)
        if (seats.reserved) {
            for (const seat of seats.reserved) {
                const floorplanSeat = findSeat(
                    draft,
                    seat.row,
                    seat.number,
                    (seat as SeatWithSection).section_id
                );

                if (floorplanSeat) {
                    floorplanSeat.isReserved = true;
                } else {
                    console.warn('Reserved seat not found:', seat.row, seat.number, (seat as SeatWithSection).section_id);
                }
            }
        }

        // Apply unavailable state (customer facing monitor)
        if (seats.unavailable) {
            for (const seat of seats.unavailable) {
                const floorplanSeat = findSeat(
                    draft,
                    seat.row,
                    seat.number,
                    (seat as SeatWithSection).section_id
                );

                if (floorplanSeat) {
                    floorplanSeat.isUnavailable = true;
                } else {
                    console.warn('Unavailable seat not found:', seat.row, seat.number, (seat as SeatWithSection).section_id);
                }
            }
        }

        for (const seat of seats.disabled) {
            const floorplanSeat = findSeat(
                draft, 
                seat.row, 
                seat.number, 
                (seat as SeatWithSection).section_id
            );

            if (floorplanSeat) {
                floorplanSeat.isDisabled = true;
            } else {
                console.warn('Seat not found:', seat.row, seat.number, (seat as SeatWithSection).section_id);
            }
        }
    });
};
